import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';
import doT, { RenderFunction } from 'bootstrap/load-dot';
import { ICOSMOSSmartInfo, IInvalidServerResponse, IServerResponse, IValidServerResponse } from './models';
import prepareDataForRender from './functions';
import $ from 'jquery';
import { isCaptchaRequired } from '../util/captcha';

const DATA_REDIRECT_URL = 'yes-url-redirect';
const DATA_NEW_INQUIRY_LABEL = 'new-inquiry-label';
const CSS_CLASS_IS_HIDDEN = 'is-hidden';
const CSS_CLASS_HIDDEN = 'hidden';
const AJAX_TYPE_POST = 'POST';
const DATA_TYPE_HTML = 'html';
const DATA_TYPE_JSON = 'json';
const EVENT_TYPE_CLICK = 'click';
const COSMOS_ACTION_SUBMIT = 'submit';
const COSMOS_ACTION_YES = 'yes';
const COSMOS_ACTION_NO = 'no';
const CAPTCHA_NEEDED_EVENT = 'captchaNeededEvent';

/**
 * COSMOSResultContainer module implementation.
 *
 * @author Nikola Tica
 * @class COSMOSResultContainer
 * @extends Tc.Module
 */
export default class COSMOSResultContainer extends Module {
	private redirectUrl! : string;
	private template! : RenderFunction;
	private $form! : JQuery;
	private $submitButton! : JQuery;
	private $yesFeedbackButton! : JQuery;
	private $noFeedbackButton! : JQuery;
	private $resultContainer! : JQuery;
	private $answerField! : JQuery;
	private $cosmosProcessIdField! : JQuery;
	private $cosmosActionField! : JQuery;
	private $showFormFieldsField! : JQuery;
	private $formEntryIdField! : JQuery;
	private $formErrorHolder! : JQuery;
	private $formCaptcha! : JQuery;
	private $ajaxCaptcha! : JQuery;
	private $buttonGroup! : JQuery;
	private $buttonIndent! : JQuery;
	private newInquiryLabel! : string;

	private $biIntegrationIdField! : JQuery;
	private $piIntegrationIdField! : JQuery;
	private $smartProcessingInfoField! : JQuery;

	/**
	 * Hook function to do all of your module stuff.
	 *
	 * @method on
	 * @param  {Hook} callback Callback function
	 * @return void
	 */
	public on(callback : Hook) {
		this.template = doT.compile(this.$ctx.find('script.tmpl-cosmos-sotions').html());
		this.$form = this.$ctx.closest('form');
		this.redirectUrl = this.$ctx.data(DATA_REDIRECT_URL);
		this.newInquiryLabel = this.$ctx.data(DATA_NEW_INQUIRY_LABEL);
		this.$submitButton = this.$ctx.find('.submit-button-holder button');
		this.$yesFeedbackButton = this.$ctx.find('.feedback-button-yes');
		this.$noFeedbackButton = this.$ctx.find('.feedback-button-no');
		this.$resultContainer = this.$ctx.find('.result-container');
		this.$answerField = this.$ctx.find('#answer');
		this.$cosmosProcessIdField = this.$ctx.find('#cosmosProcessId');
		this.$cosmosActionField = this.$ctx.find('#cosmosAction');
		this.$showFormFieldsField = this.$ctx.find('#showFormFields');
		this.$formEntryIdField = this.$form.find('[name=":formEntryId"]');
		this.$formErrorHolder = this.$form.find('.headingError');
		this.$formCaptcha = this.$form.find('#captcha');
		this.$ajaxCaptcha = this.$ctx.find('.modAjaxCaptcha');
		// Exclude empty button-groups, button-group in COSMOSResultContainer itself and in personalizationcontainer normal
		// Use to display Captcha.
		this.$buttonGroup = this.$form.find('.button-group:not(.modCOSMOSResultContainer .button-group)'
			+ ':not(.personalizationcontainer .normal .button-group):has(button)');
		this.$buttonIndent = this.$form.find('.button-indent');

		// Avoid migration script
		this.$biIntegrationIdField = $('<input type="hidden" name="biIntegrationId">').appendTo(this.$form);
		this.$piIntegrationIdField = $('<input type="hidden" name="piIntegrationId">').appendTo(this.$form);
		this.$smartProcessingInfoField = $('<input type="hidden" name="smartProcessingInfo">').appendTo(this.$form);

		// Detach form captcha, use ajaxcaptcha instead
		if (this.$formCaptcha.length > 0) {
			this.$formCaptcha.detach();
		}

		if (!this.$ajaxCaptcha.find('#g-recaptcha-response').length) {
			/**
			 * Workaround for reseting initial ajaxCaptcha issues:
			 * When you reach condition that ajax captcha is needed and then reload
			 * page, you will trigger regular captcha which is detached and ajax
			 * captcha will not be visible but it will have required validation state.
			 * This will prevent submiting the form but since ajaxCaptcha will not be
			 * visible the user willnot be able to fix it and it will stay in limbo
			 * state withouth any visible notification why.
			 */
			const captchaCv = this.$ajaxCaptcha.find('[data-clientvalidation]').data('clientvalidation');
			captchaCv.required = false;
			this.$ajaxCaptcha.find('.captcha').data('clientvalidation', captchaCv);
		}

		this.$submitButton.on(EVENT_TYPE_CLICK, this.submitToCosmos.bind(this));
		callback();
	}

	/**
	 * This function attaches handlers to component feedback buttons.
	 *
	 * @method attachFeedbackHandlers
	 * @private
	 * @return void
	 */
	private attachFeedbackHandlers() {
		this.$yesFeedbackButton.on(EVENT_TYPE_CLICK, this.sendYesFeedback.bind(this));
		this.$noFeedbackButton.on(EVENT_TYPE_CLICK, this.sendNoFeedback.bind(this));
	}

	/**
	 * This function renders data retrieved from COSMOS service.
	 *
	 * @method renderResults
	 * @private
	 * @param  {ICOSMOSSmartInfo} data COSMOS response data
	 * @return void
	 */
	private renderResults(data : ICOSMOSSmartInfo) {
		const $holder = this.$ctx.find('.solutions-holder');
		prepareDataForRender(data);

		$holder.empty();
		const $html = $(this.template(data));
		$holder.append($html);

		const $feedbackButtonGroup = this.$ctx.find('.solutions-feedback .button-indent');
		this.$ajaxCaptcha.detach();
		$feedbackButtonGroup.before(this.$ajaxCaptcha);

		return;
	}

	/**
	 * This function submits data to COSMOS.
	 *
	 * @method submitToCosmos
	 * @private
	 * @return void
	 */
	private submitToCosmos(event : Event) {
		const mod = this;
		const formApi = this.$form.parent().data('form-api');
		mod.hideFormFields();
		event.preventDefault();

		setTimeout(() => {
			const invalidateFieldsNum = formApi.validateForm();
			mod.$formErrorHolder.find('.msg').text('');
			mod.$formErrorHolder.addClass(CSS_CLASS_HIDDEN);
			mod.hideResultContainer();

			if (!invalidateFieldsNum) {
				mod.hideSubmitButton();
				mod.setCosmosActionFieldValue(COSMOS_ACTION_SUBMIT);
				$.ajax({
					data: mod.$form.serialize(),
					dataType: DATA_TYPE_JSON,
					type: AJAX_TYPE_POST,
					url: this.$form.attr('action'),
					success(data : IServerResponse) {
						if ('formEntryId' in data) {
							mod.$formEntryIdField.val(data.formEntryId);
						}

						if (data.success) {
							mod.changeSubmitButtonLabel(mod.newInquiryLabel);
							mod.processData(data);
						} else {
							mod.handleExceptions(data);
						}
					},
					error(data : JQuery.jqXHR) {
						mod.handleExceptions(data.responseJSON);
					},
				});
			}
		}, 250);
	}

	private handleExceptions(data : IInvalidServerResponse) {
		if (isCaptchaRequired(data.data || [])) {
			// Show captcha
			this.$ajaxCaptcha.trigger(CAPTCHA_NEEDED_EVENT);
			this.showSubmitButton();
		} else {
			this.handleInvalidResponse();
		}
	}

	private handleInvalidResponse() {
		this.changeSubmitButtonLabel(this.newInquiryLabel);
		this.showSubmitButton();
		// Display fields
		this.showFormFields();
	}

	/**
	 * This function process data retrieved from COSMOS service.
	 *
	 * @method processData
	 * @private
	 * @param  {ICOSMOSSmartInfo} data
	 * @return void
	 */
	private processData(data : IValidServerResponse) {
		if (data.smartInfo && data.smartInfo.info) {
			this.$cosmosProcessIdField.val(data.smartInfo.info.cosmosProcessID);
			this.$biIntegrationIdField.val(data.smartInfo.biIntegrationId);
			this.$piIntegrationIdField.val(data.smartInfo.piIntegrationId);
			this.$smartProcessingInfoField.val(JSON.stringify(data.smartInfo.info));
		}
		if (!data.smartInfo
			|| !data.smartInfo.info
			|| !data.smartInfo.info.suggestedTopics
			|| !data.smartInfo.info.suggestedTopics.length) {
			this.handleInvalidResponse();

			return;
		}
		this.renderResults(data.smartInfo.info);
		this.attachFeedbackHandlers();
		this.showResultContainer();
		this.showSubmitButton();
	}

	/**
	 * This function triggers visibility content change
	 *
	 * @method showFormFields
	 * @return void
	 */
	private showFormFields() {
		this.setCosmosActionFieldValue('');
		this.$showFormFieldsField.val('true');
		this.$showFormFieldsField.trigger('change');

		this.$ajaxCaptcha.detach();
		if (this.$buttonGroup.length > 0) {
			this.$buttonGroup.before(this.$ajaxCaptcha);
		} else if (this.$buttonIndent) {
			this.$buttonIndent.before(this.$ajaxCaptcha);
		}
	}

	/**
	 * This function triggers visibility content change
	 *
	 * @method hideFormFields
	 * @return void
	 */
	private hideFormFields() {
		this.setCosmosActionFieldValue('');
		this.$showFormFieldsField.val('false');
		this.$showFormFieldsField.trigger('change');

		this.$ajaxCaptcha.detach();
		this.$submitButton.before(this.$ajaxCaptcha);
	}

	/**
	 * This function sends answer yes feedback to COSMOS and redirect the form to
	 * the new url.
	 *
	 * @method sendYesFeedback
	 * @private
	 * @return void
	 */
	private sendYesFeedback(event : Event) {
		const mod = this;
		event.preventDefault();
		mod.setAnswerFieldValue('yes');
		mod.setCosmosActionFieldValue(COSMOS_ACTION_YES);
		$.ajax({
			data: mod.$form.serialize(),
			dataType: DATA_TYPE_HTML,
			type: AJAX_TYPE_POST,
			url: mod.$form.attr('action'),
			success(data : IServerResponse) {
				if (!data.success && isCaptchaRequired(data.data || [])) {
					// Show captcha
					mod.$ajaxCaptcha.trigger(CAPTCHA_NEEDED_EVENT);
				} else {
					mod.yesRedirect();
				}
			},
			error(data : JQuery.jqXHR) {
				if (isCaptchaRequired(data.responseJSON.data || [])) {
					// Show captcha
					mod.$ajaxCaptcha.trigger(CAPTCHA_NEEDED_EVENT);
				} else {
					mod.yesRedirect();
				}
			},
		});
	}

	/**
	 * This function redirect to yes page.
	 */
	private yesRedirect() : void {
		if (this.redirectUrl) {
			window.location.assign(this.redirectUrl);
		}
		this.hideResultContainer();
	}

	/**
	 * This function sends answer no feedback to COSMOS.
	 *
	 * @method sendNoFeedback
	 * @private
	 * @return void
	 */
	private sendNoFeedback(event : Event) {
		const mod = this;
		event.preventDefault();
		mod.setAnswerFieldValue('no');
		mod.setCosmosActionFieldValue(COSMOS_ACTION_NO);
		mod.hideSubmitButton();

		$.ajax({
			data: mod.$form.serialize(),
			dataType: DATA_TYPE_HTML,
			type: AJAX_TYPE_POST,
			url: mod.$form.attr('action'),
			success(data : IServerResponse) {
				if (!data.success && isCaptchaRequired(data.data || [])) {
					// Show captcha
					mod.$ajaxCaptcha.trigger(CAPTCHA_NEEDED_EVENT);
				} else {
					mod.hideResultContainer();
					mod.showFormFields();
				}
			},
			error(data : JQuery.jqXHR) {
				if (isCaptchaRequired(data.responseJSON.data || [])) {
					// Show captcha
					mod.$ajaxCaptcha.trigger(CAPTCHA_NEEDED_EVENT);
				} else {
					mod.hideResultContainer();
					mod.showFormFields();
				}
			},
		});
	}

	/**
	 * This function set hidden answer field value.
	 *
	 * @method: setAnswerFieldValue
	 * @private
	 * @param {String} val Expected values are "yes" or "no"
	 * @return void
	 */
	private setAnswerFieldValue(val : string) {
		this.$answerField.val(val);
	}

	/**
	 * This function set hidden cosmosAction field value.
	 *
	 * @method setCosmosActionFieldValue
	 * @private
	 * @param  {string} value Expected values are: submit, no, yes and empty
	 * string ''
	 * @return void
	 */
	private setCosmosActionFieldValue(value : string) {
		this.$cosmosActionField.val(value);
	}

	/**
	 * This function hides result container.
	 *
	 * @method: hideResultContainer
	 * @private
	 * @return void
	 */
	private hideResultContainer() {
		this.$resultContainer.addClass(CSS_CLASS_IS_HIDDEN);
	}

	/**
	 * This function displays result container.
	 *
	 * @method: showResultContainer
	 * @private
	 * @return void
	 */
	private showResultContainer() {
		this.$resultContainer.removeClass(CSS_CLASS_IS_HIDDEN);
	}

	/**
	 * This function hides submit button.
	 *
	 * @method hideSubmitButton
	 * @private
	 * @return void
	 */
	private hideSubmitButton() {
		this.$submitButton.addClass(CSS_CLASS_IS_HIDDEN);
	}

	/**
	 * This function shows submit button.
	 *
	 * @method showSubmitButton
	 * @private
	 * @return void
	 */
	private showSubmitButton() {
		this.$submitButton.removeClass(CSS_CLASS_IS_HIDDEN);
	}

	private changeSubmitButtonLabel(newLabel : string) {
		this.$submitButton.text(newLabel);
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/cosmos/Tc.Module.COSMOSResultContainer.ts